// The level picker of the build card (mockup round 084 card 17, R1839;
// ui-map-interface.md §8 "The build card").
//
// The Empire frame previews the level the BUILD press buys alone (R1081).
// A building card therefore asks the server once for the levels past that
// one (`PreviewQuery`), and the answer (`BuildingPreview`) states every
// figure at each level up to the cap, read through the real rule. The card
// draws a tab strip of the level numbers (R1864, R2011) and the compare
// grid of the card kit with three columns: NOW, NEXT and AT k.
//
// `LevelPreviews` of `levelcache.ts` holds the answers. The client
// computes no figure: every value is the server's.

import { JobKind } from '../../gen/proto/aquaphobia';
import type { BuildingLevel, BuildingPreview, LevelPreview } from '../../gen/proto/aquaphobia';
import { card as CS } from '../../gen/strings';
import { type GameState, costText, dur } from '../../state';
import { esc } from '../esc';
import { tabStrip } from '../choice/choice';
import { type CompareRow, compareHtml } from '../cards/compare';
import { effectLabel, effectValue } from './markup';

/** The levels the picker offers, and the one it shows: `pick`, or the
 *  first level past the next when `pick` is not one of them. */
function pickedLevel(p: BuildingPreview, pick: number): LevelPreview | null {
  return p.levels.find((l) => l.level === pick) ?? p.levels[0] ?? null;
}

/**
 * The picker and the grid of one building (084 card 17), or '' when the
 * answer holds no level past the next. `bl` is the Empire frame's row: its
 * effects carry NOW and NEXT, and the picked level carries AT k. A figure
 * row marks the AT column as the better one, because a level step moves
 * every figure the right way; the cost and the time rows mark none.
 */
export function levelPickHtml(s: GameState, bl: BuildingLevel, p: BuildingPreview, pick: number): string {
  const at = pickedLevel(p, pick);
  if (!at || bl.maxed) return '';
  const next = bl.level + bl.queued + 1;
  const rows: CompareRow[] = [];
  bl.effects.forEach((e, i) => {
    const label = effectLabel(e.kind);
    const a = at.effects[i];
    if (!label || !a || a.kind !== e.kind) return;
    rows.push({
      key: label,
      vals: [esc(effectValue(e.kind, e.now)), esc(effectValue(e.kind, e.next)), esc(effectValue(e.kind, a.next))],
      best: a.next !== e.now ? 2 : -1,
    });
  });
  const take = (sec: number) => esc(dur(s.buildSec(JobKind.JOB_KIND_BUILDING, sec)));
  rows.push(
    { key: CS.levels_cost, vals: ['—', costText(bl.nextCost), costText(at.cost)], best: -1 },
    { key: CS.levels_time, vals: ['—', take(bl.nextTimeSec), take(at.timeSec)], best: -1 },
  );
  // A preview level saves nothing, so the picker is a filter: a compact
  // tab strip of the choice kit (R2011). A tab states the level number
  // alone, so ten tabs fit a card of 300 px; its title says the level in
  // words (R1864). The id of a tab is "building:level".
  const tabs = p.levels.map((l) => ({
    id: `${bl.building}:${l.level}`, label: CS.levels_pick({ level: l.level }),
    title: CS.levels_pick_label({ level: l.level }),
  }));
  return `<div class="ck-sect"><span class="ck-cap">${esc(CS.levels)}</span>`
    + tabStrip(tabs, `${bl.building}:${at.level}`, 'lvlpick', { label: CS.levels, size: 'compact' }) + '</div>'
    + compareHtml([
      CS.levels_now({ level: bl.level }), CS.levels_next({ level: next }), CS.levels_at({ level: at.level }),
    ], rows);
}
